import { useCallback, useRef } from 'react';
import { useTrackViews, AnalyticsTargetType } from './useTrackViews';

interface UseTrackProfileViewOptions {
  /** The profile/store ID to track */
  profileId: string | null;
}

/**
 * Hook for tracking profile views
 * Tracks when users view athlete/store profiles
 */
export function useTrackProfileView({ profileId }: UseTrackProfileViewOptions) {
  const hasTrackedRef = useRef(false);
  const trackedProfileRef = useRef<string | null>(null);

  const { trackDirectView, loading } = useTrackViews({
    targetType: AnalyticsTargetType.PROFILE,
  });

  // Track profile view (only once per profile visit)
  const trackProfileView = useCallback(async () => {
    if (!profileId) return;

    // Don't track if already tracked for this profile
    if (hasTrackedRef.current && trackedProfileRef.current === profileId) {
      return;
    }

    hasTrackedRef.current = true;
    trackedProfileRef.current = profileId;

    await trackDirectView(profileId);
  }, [profileId, trackDirectView]);

  // Reset tracker (call when navigating to a different profile)
  const reset = useCallback(() => {
    hasTrackedRef.current = false;
    trackedProfileRef.current = null;
  }, []);

  return {
    trackProfileView,
    reset,
    loading,
  };
}

export default useTrackProfileView;
